// src/features/chat/MessageSearchPanel.tsx
//
// Full-text message search (chat.SearchMessages). Used two ways:
//   - scoped to one conversation (search icon in ChatDetailPage's header)
//   - unscoped, across every conversation the user is a member of (search
//     icon in ChatLayout's sidebar header)
//
// SearchMessages is a GET whose generated client only exposes pagination
// fields (`limit`/`cursor`/`direction`/`sort`) — the query/conversation/
// sender/date/content-type/pinned filters declared on SearchMessagesRequest
// never made it into the generated function signature. Same class of gap
// documented in docs/ux/resource-ia.md and docs/ux/search-ia.md ("codegen
// gaps... fixed at the call site"): we call the client directly instead of
// routing through chatApi.SearchMessages.

import { useEffect, useState } from 'react';
import { getApiClient } from '@core/api';
import { useI18n } from '@react/hooks';
import type { SearchMessageHit, SearchMessagesResponse } from '@core/types/chat';
import { formatMessageTime } from './logic';

export interface MessageSearchPanelProps {
  /** Restrict results to this conversation. Omit for cross-conversation search. */
  scopeConversationId?: string;
  locale: string;
  onClose: () => void;
  /** Navigate to a hit — conversation id + message id to deep-link/jump to. */
  onJump: (conversationId: string, messageId: string) => void;
}

export function MessageSearchPanel({ scopeConversationId, locale, onClose, onJump }: MessageSearchPanelProps) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [hits, setHits] = useState<SearchMessageHit[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = async (q: string, pinned: boolean) => {
    if (!q.trim()) {
      setHits([]);
      setHasSearched(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const client = getApiClient();
      const params: Record<string, string | number | boolean> = { query: q.trim(), limit: 30 };
      if (scopeConversationId) params.conversation_id = scopeConversationId;
      if (pinned) params.pinned_only = true;
      const res = await client.get<SearchMessagesResponse>('/chat/messages/search', { params });
      setHits(res.hits ?? []);
      setTotal(res.total_count ?? res.hits?.length ?? 0);
      setHasSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unknown'));
      setHits([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced live search as the user types.
  useEffect(() => {
    const handle = window.setTimeout(() => runSearch(query, pinnedOnly), 350);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, pinnedOnly, scopeConversationId]);

  return (
    <div className="flex h-full w-80 flex-col border-l border-border bg-background">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">
          {scopeConversationId ? t('chat.search.titleScoped') : t('chat.search.title')}
        </h3>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="border-b border-border p-3 space-y-2">
        <div className="relative">
          <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('chat.search.placeholder')}
            className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-2 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={pinnedOnly} onChange={e => setPinnedOnly(e.target.checked)} className="rounded border-border" />
          {t('chat.search.pinnedOnly')}
        </label>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : error ? (
          <div className="p-4 text-sm text-destructive">{error}</div>
        ) : !hasSearched ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">{t('chat.search.prompt')}</div>
        ) : hits.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">{t('chat.search.noResults')}</div>
        ) : (
          <>
            <p className="px-4 pt-2 text-xs text-muted-foreground">{t('chat.search.resultCount', { count: total })}</p>
            <ul className="divide-y divide-border">
              {hits.map((hit, idx) => {
                const messageId = hit.message?.entity?.id;
                const conversationId = hit.conversation?.id;
                return (
                  <li key={`${messageId}-${idx}`}>
                    <button
                      type="button"
                      onClick={() => {
                        if (conversationId && messageId) onJump(conversationId, messageId);
                      }}
                      className="block w-full px-4 py-2.5 text-left hover:bg-muted/50 transition-colors"
                    >
                      {!scopeConversationId && hit.conversation_name && (
                        <p className="text-xs font-semibold text-primary truncate">{hit.conversation_name}</p>
                      )}
                      <p className="text-xs font-medium text-foreground truncate">
                        {hit.message?.sender?.display_name || t('chat.unknownUser')}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                        {hit.highlight || hit.message?.content || ''}
                      </p>
                      {hit.message?.created_at && (
                        <p className="mt-0.5 text-[10px] text-muted-foreground">
                          {formatMessageTime(hit.message.created_at, locale)}
                        </p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

export default MessageSearchPanel;
